import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faStore,
  faLocationDot,
  faEye,
  faBoxOpen,
  faStar,
  faTag,
  faFire,
  faCrown,
  faCommentDots,
  faXmark,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "../hooks/useHerKart";

const StoreCard = ({ store, onView }) => {
  const [imageError, setImageError] = useState(false);
  const { fetchFeedbackByRef, submitFeedback } = useHerKart();

  // NEW — store-level feedback state
  const [showReviews, setShowReviews] = useState(false);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);
  const [avgRating, setAvgRating] = useState(store?.rating || 0);
  const [totalRatings, setTotalRatings] = useState(store?.totalRatings || 0);

  const [newRating, setNewRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [newMessage, setNewMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState(null);

  const image = useMemo(() => {
    if (!imageError && store?.banner) {
      return store.banner;
    }

    if (!imageError && store?.logo) {
      return store.logo;
    }

    return "https://placehold.co/800x600/18181b/f9a8d4?text=HerKart";
  }, [store, imageError]);

  const loadFeedback = async () => {
    if (!store?.storeCode) return;

    setLoadingFeedback(true);
    try {
      const response = await fetchFeedbackByRef("store", store.storeCode);

      if (response?.success) {
        setFeedbackList(response.feedback || []);
        setAvgRating(response.avgRating || 0);
        setTotalRatings(response.totalRatings || 0);
      }
    } catch (e) {
      console.error("Store feedback fetch error:", e);
    } finally {
      setLoadingFeedback(false);
    }
  };

  useEffect(() => {
    // Pull the real average once on mount so the badge on the card itself is accurate,
    // not just when the reviews popover is opened.
    loadFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.storeCode]);

  const handleOpenReviews = (e) => {
    e.stopPropagation();
    setShowReviews(true);
    setFeedbackMsg(null);
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!newRating && !newMessage.trim()) {
      setFeedbackMsg({ type: "error", text: "Please give a rating or write a review." });
      return;
    }

    if (!store?.storeCode) return;

    setSubmitting(true);
    try {
      const response = await submitFeedback({
        publisherCode: store.ownerCode || store.storeCode, // falls back to storeCode if ownerCode isn't present on this shape
        level: "store",
        refCode: store.storeCode,
        refName: store.store,
        rating: newRating,
        message: newMessage.trim(),
      });

      if (response?.success) {
        setNewRating(0);
        setNewMessage("");
        setFeedbackMsg({ type: "success", text: "Thanks for your review!" });
        loadFeedback();
      } else {
        setFeedbackMsg({ type: "error", text: "Failed to submit review." });
      }
    } catch (e) {
      console.error("Store feedback submit error:", e);
      setFeedbackMsg({ type: "error", text: "Failed to submit review." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="group overflow-hidden rounded-3xl border border-fuchsia-500/15 bg-gradient-to-br from-neutral-900 via-neutral-900 to-black shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-pink-500/40">
      <div className="relative">
        <img
          src={image}
          alt={store?.store}
          loading="lazy"
          onError={() => setImageError(true)}
          className="h-52 w-full object-cover transition duration-500 group-hover:scale-105"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          {store?.featured && (
            <span className="flex items-center gap-1 rounded-full bg-pink-600 px-3 py-1 text-xs font-semibold text-white shadow-lg">
              <FontAwesomeIcon icon={faCrown} />
              Featured
            </span>
          )}

          {store?.bestseller && (
            <span className="flex items-center gap-1 rounded-full bg-fuchsia-700 px-3 py-1 text-xs font-semibold text-white shadow-lg">
              <FontAwesomeIcon icon={faFire} />
              Popular
            </span>
          )}
        </div>
      </div>

      <div className="space-y-4 p-5">
        <div className="flex items-center justify-between">
          <span className="rounded-full bg-fuchsia-900/40 px-3 py-1 text-xs font-semibold text-fuchsia-300">
            <FontAwesomeIcon icon={faTag} className="mr-2" />

            {store?.categories?.[0] || "General"}
          </span>

          <button
            type="button"
            onClick={handleOpenReviews}
            className="flex items-center gap-1 rounded-full bg-black/20 px-2 py-1 text-sm text-amber-400 transition hover:bg-black/40"
            title="View / write reviews"
          >
            <FontAwesomeIcon icon={faStar} />
            <span className="font-semibold">{avgRating || 0}</span>
            <span className="text-xs text-gray-400">({totalRatings || 0})</span>
          </button>
        </div>

        <div>
          <h3 className="line-clamp-2 text-lg font-bold leading-6 text-white">
            {store?.store}
          </h3>

          <p className="mt-2 text-sm text-pink-300">{store?.entrepreneur}</p>
        </div>

        <div className="space-y-2 text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faStore} className="text-pink-400" />

            <span className="truncate">{store?.storeCode}</span>
          </div>

          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faLocationDot} className="text-pink-400" />

            <span>
              {store?.locality}, {store?.city}
            </span>
          </div>
        </div>

        <div className="rounded-2xl border border-fuchsia-500/10 bg-white/[0.03] p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Products
              </div>

              <div className="text-3xl font-extrabold text-white">
                {store?.productCount}
              </div>
            </div>

            <div className="text-right">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Categories
              </div>

              <div className="font-semibold text-gray-200">
                {store?.categories?.length}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {store?.categories?.map((category) => (
            <span
              key={category}
              className="rounded-full border border-pink-500/20 bg-pink-500/10 px-3 py-1 text-xs font-medium text-pink-300"
            >
              {category}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between rounded-2xl border border-fuchsia-500/10 bg-black/20 px-4 py-3">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faBoxOpen} className="text-emerald-400" />

            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Catalogue
              </div>

              <div className="text-sm font-semibold text-emerald-400">
                {store?.productCount} Products
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleOpenReviews}
              className="flex items-center gap-2 rounded-xl border border-amber-500/20 w-fit px-2 py-1 text-sm font-medium text-amber-300 transition hover:bg-amber-500/10"
            >
              <FontAwesomeIcon icon={faCommentDots} />
              Reviews
            </button>

            <button
              type="button"
              onClick={() => onView?.(store)}
              className="flex items-center gap-2 rounded-xl border border-fuchsia-500/20 w-fit px-2 py-1 text-sm font-medium text-fuchsia-300 transition hover:bg-fuchsia-500/10"
            >
              <FontAwesomeIcon icon={faEye} />
              Visit
            </button>
          </div>
        </div>
      </div>

      {/* NEW — Store-level reviews popover */}
      {showReviews && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={(e) => {
            e.stopPropagation();
            setShowReviews(false);
          }}
        >
          <div
            className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-3xl border border-fuchsia-500/20 bg-neutral-950 p-5 text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-cyan-200">
                {store?.store} — Reviews
              </h3>
              <button onClick={() => setShowReviews(false)} className="text-slate-400 hover:text-white">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="mt-2 flex items-center gap-2 text-amber-400">
              <FontAwesomeIcon icon={faStar} />
              <span className="font-semibold">{avgRating || 0}</span>
              <span className="text-xs text-gray-400">({totalRatings || 0} ratings)</span>
            </div>

            <form onSubmit={handleSubmitFeedback} className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4">
              <p className="mb-2 text-sm font-semibold text-pink-300">Rate this store</p>

              <div className="flex gap-1 text-xl">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    type="button"
                    key={star}
                    onClick={() => setNewRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className={(hoverRating || newRating) >= star ? "text-amber-400" : "text-slate-600"}
                  >
                    <FontAwesomeIcon icon={faStar} />
                  </button>
                ))}
              </div>

              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value.slice(0, 250))}
                placeholder="Share your experience with this store..."
                rows={2}
                className="mt-3 w-full rounded-xl border border-white/10 bg-[#0b1120] p-2 text-sm text-white outline-none focus:border-pink-500"
              />

              {feedbackMsg && (
                <p className={`mt-2 text-xs ${feedbackMsg.type === "success" ? "text-emerald-300" : "text-red-300"}`}>
                  {feedbackMsg.text}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mt-3 flex items-center gap-2 rounded-xl bg-pink-600 px-4 py-2 text-sm font-semibold hover:bg-pink-500 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                {submitting ? "Submitting..." : "Submit Review"}
              </button>
            </form>

            <div className="mt-4 space-y-3">
              {loadingFeedback ? (
                <p className="text-sm text-slate-400">Loading reviews...</p>
              ) : feedbackList.length === 0 ? (
                <p className="text-sm text-slate-400">No reviews yet — be the first!</p>
              ) : (
                feedbackList.map((f) => (
                  <div key={f._id} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-white">{f.userName}</span>
                      {f.rating > 0 && (
                        <span className="flex items-center gap-1 text-xs text-amber-400">
                          <FontAwesomeIcon icon={faStar} /> {f.rating}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-slate-300">{f.message}</p>
                    {f.sellerReply && (
                      <p className="mt-2 rounded-lg bg-pink-500/10 p-2 text-xs text-pink-200">
                        <span className="font-semibold">Store reply: </span>
                        {f.sellerReply}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreCard;
